import { useApolloClient, useLazyQuery, useReadQuery, type QueryRef } from '@apollo/client/react';
import { ExternalLink, CalendarClock } from 'lucide-react';
import { toast } from 'sonner';
import { memo, useCallback } from 'react';

import { BillingCard, BillingPlanDropdown } from 'src/components/Billing';
import { withSuspense } from 'src/hoc';
import { useCancellationPlan } from 'src/context/billing';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { type IBillingSubscription } from 'src/graphql/schema';
import { InvoicesDocument } from 'src/graphql/actions/invoices/invoices.query';

import { loadBlockingApps } from '../Cancellation/api/load-blocking-apps';

import type { IBillingQuery } from 'src/graphql/actions/billing/billing.query';

import { AddCouponDialog, Subscriptions } from './ui';

type Props = {
  queryRef: QueryRef<IBillingQuery>;
  cancellationRequested: boolean;
};

const CancelPlanBanner = () => {
  return (
    <Alert variant="default" className="bg-orange-50 text-orange-800 border-orange-50">
      <CalendarClock />
      <AlertTitle className="font-bold">Account changes are in progress</AlertTitle>
      <AlertDescription className="text-orange-800/70">
        You’ve scheduled updates to your account. These are being reviewed or processed according to your account terms.
      </AlertDescription>
    </Alert>
  );
};

const DropdownContainer = memo(
  ({ canRequestCancellation, onUpgradePlan }: { canRequestCancellation: boolean; onUpgradePlan: () => void }) => {
    const [loadInvoices, { loading: loadingInvoices }] = useLazyQuery(InvoicesDocument, {
      fetchPolicy: 'cache-and-network',
    });
    const client = useApolloClient();
    const {
      ui: { setStep, setBlockingApps },
      exclusiveOfferApplied,
    } = useCancellationPlan();

    const onCancelPlan = async (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      e.preventDefault();
      document.documentElement.style.cursor = 'progress';
      document.body.style.pointerEvents = 'none';

      try {
        // Check for unpaid invoices first
        const { data: invoicesData } = await loadInvoices({ variables: { unpaid: true } });
        const hasUnpaidInvoices = Boolean(invoicesData?.invoices?.nodes?.length);
        if (hasUnpaidInvoices) return setStep('unpaid-invoices');

        // Check for owned apps with active protections
        const blockingApps = await loadBlockingApps(client);
        setBlockingApps(blockingApps);

        if (blockingApps.length) return setStep('show-plan-usage-warning');
        if (exclusiveOfferApplied) return setStep('show-downgrade-confirmation');
        return setStep('show-cancellation-prompt');
      } catch {
        toast.error('Failed to cancel plan');
      } finally {
        document.documentElement.style.cursor = '';
        document.body.style.pointerEvents = '';
      }
    };

    return (
      <BillingPlanDropdown
        onSeePricingDetails={onUpgradePlan}
        onCancelPlan={onCancelPlan}
        loading={{
          cancel: loadingInvoices,
        }}
        {...(!canRequestCancellation && {
          disable: {
            cancel:
              'Changes are still being applied to your account. Please allow 30 days before requesting new changes.',
          },
        })}
      />
    );
  },
);

DropdownContainer.displayName = 'DropdownContainer';

const BillingSubscriptionsContainer = ({ queryRef, cancellationRequested = false }: Props) => {
  const {
    data: { billing },
  } = useReadQuery(queryRef);

  const activeSubscriptions = (billing?.subscriptions?.filter((subscription) => subscription.status === 'active') ??
    []) as IBillingSubscription[];

  const onUpgradePlan = useCallback(() => window.open('https://codekeeper.co/pricing', '_blank'), []);

  return (
    <BillingCard
      title="Current Plan"
      className="mb-4"
      contentClassName="space-y-6"
      action={
        <DropdownContainer
          canRequestCancellation={billing?.policy.requestCancellation ?? false}
          onUpgradePlan={onUpgradePlan}
        />
      }
    >
      {cancellationRequested && <CancelPlanBanner />}
      <Subscriptions subscriptions={activeSubscriptions} />
      {Boolean(activeSubscriptions.length) && (
        <div className="flex flex-col sm:flex-row justify-end gap-3">
          {billing?.policy.applyCoupon && <AddCouponDialog />}
          <Button onClick={onUpgradePlan}>
            Upgrade plan <ExternalLink />
          </Button>
        </div>
      )}
    </BillingCard>
  );
};

const SubscriptionSkeleton = () => (
  <BillingCard title="Current Plan" className="mb-4">
    <div className="space-y-6">
      <div className="space-y-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <Skeleton key={index} className="h-4 w-full" />
        ))}
      </div>
      <Separator />
      <div className="space-y-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <Skeleton key={index} className="h-4 w-full" />
        ))}
      </div>
    </div>
  </BillingCard>
);

export default withSuspense(BillingSubscriptionsContainer, SubscriptionSkeleton);
